import React, { useCallback, useEffect, useRef } from 'react';
import { createFocusTrap, FocusTrap } from 'focus-trap';
import { copyFor } from 'config';
import {
  StyledModal,
  StyledModalContent,
  StyledModalHeader,
  CloseButton,
} from './modal.style';

const getCopy = copyFor('buttons');

interface Props {
  children?: React.ReactNode;
  onClose: () => void;
  textAlign?: string;
  width?: number;
}

export const Modal = (props: Props) => {
  const { onClose, children } = props;
  const trap = useRef<FocusTrap | null>(null);

  const modalRef = useCallback(
    node => {
      if (node !== null) {
        // This approach assumes our pattern of only mounting one modal at a time
        trap.current = createFocusTrap(node, {
          clickOutsideDeactivates: true,
          onDeactivate: onClose,
        });
        try {
          trap.current.activate();
        } catch (e) {
          // We don't need to do anything with the "error". Focus trap activation will happen after the modal data is loaded.
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (trap.current) trap.current.deactivate();
  }, []);

  return (
    <StyledModal {...props} onBackdropClick={onClose}>
      <StyledModalContent ref={modalRef} id="inside-modal">
        <StyledModalHeader>
          <CloseButton
            aria-label={getCopy('closeModal')}
            icon="UIClose"
            onClick={onClose}
          />
        </StyledModalHeader>

        {children}
      </StyledModalContent>
    </StyledModal>
  );
};
